import { useCallback, useRef } from 'react';

const useNotificationSound = () => {
  const audioContextRef = useRef(null);
  const isPlayingRef = useRef(false);

  const playNotificationSound = useCallback(() => {
    // Éviter les sons multiples simultanés
    if (isPlayingRef.current) return;

    try {
      isPlayingRef.current = true;

      // Créer un contexte audio
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;

      // Mélodie fun et reconnaissable - style "ding dong" restaurant
      const melody = [
        // Premier accord joyeux (arpège montant)
        { freq: 523.25, start: 0, duration: 0.15, gain: 0.4 },      // Do5
        { freq: 659.25, start: 0.1, duration: 0.15, gain: 0.4 },    // Mi5
        { freq: 783.99, start: 0.2, duration: 0.2, gain: 0.5 },     // Sol5
        { freq: 1046.50, start: 0.3, duration: 0.4, gain: 0.5 },    // Do6

        // Pause puis répétition
        { freq: 783.99, start: 0.9, duration: 0.15, gain: 0.4 },    // Sol5
        { freq: 1046.50, start: 1.0, duration: 0.15, gain: 0.4 },   // Do6
        { freq: 1318.51, start: 1.1, duration: 0.3, gain: 0.5 },    // Mi6

        // Deuxième séquence (après 1.8s)
        { freq: 523.25, start: 1.8, duration: 0.15, gain: 0.4 },    // Do5
        { freq: 659.25, start: 1.9, duration: 0.15, gain: 0.4 },    // Mi5
        { freq: 783.99, start: 2.0, duration: 0.2, gain: 0.5 },     // Sol5
        { freq: 1046.50, start: 2.1, duration: 0.4, gain: 0.5 },    // Do6

        // Final joyeux
        { freq: 783.99, start: 2.7, duration: 0.15, gain: 0.4 },    // Sol5
        { freq: 1046.50, start: 2.8, duration: 0.15, gain: 0.4 },   // Do6
        { freq: 1318.51, start: 2.9, duration: 0.5, gain: 0.5 },    // Mi6

        // Troisième séquence (après 3.6s)
        { freq: 523.25, start: 3.6, duration: 0.15, gain: 0.35 },   // Do5
        { freq: 659.25, start: 3.7, duration: 0.15, gain: 0.35 },   // Mi5
        { freq: 783.99, start: 3.8, duration: 0.2, gain: 0.4 },     // Sol5
        { freq: 1046.50, start: 3.9, duration: 0.4, gain: 0.45 },   // Do6

        // Final
        { freq: 783.99, start: 4.5, duration: 0.15, gain: 0.35 },   // Sol5
        { freq: 1046.50, start: 4.6, duration: 0.15, gain: 0.35 },  // Do6
        { freq: 1318.51, start: 4.7, duration: 0.5, gain: 0.4 },    // Mi6
      ];

      melody.forEach(note => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.frequency.value = note.freq;
        oscillator.type = 'sine';

        const startTime = audioContext.currentTime + note.start;

        // Envelope ADSR simple pour un son plus agréable
        gainNode.gain.setValueAtTime(0, startTime);
        gainNode.gain.linearRampToValueAtTime(note.gain, startTime + 0.02); // Attack
        gainNode.gain.linearRampToValueAtTime(note.gain * 0.7, startTime + 0.05); // Decay
        gainNode.gain.linearRampToValueAtTime(note.gain * 0.5, startTime + note.duration - 0.05); // Sustain
        gainNode.gain.linearRampToValueAtTime(0, startTime + note.duration); // Release

        oscillator.start(startTime);
        oscillator.stop(startTime + note.duration);
      });

      // Ajouter un léger effet de cloche (harmonique)
      const bellTimes = [0, 0.9, 1.8, 2.7, 3.6, 4.5];
      bellTimes.forEach(time => {
        const bell = audioContext.createOscillator();
        const bellGain = audioContext.createGain();

        bell.connect(bellGain);
        bellGain.connect(audioContext.destination);

        bell.frequency.value = 2093; // Do7 (harmonique haute)
        bell.type = 'sine';

        const startTime = audioContext.currentTime + time;
        bellGain.gain.setValueAtTime(0.15, startTime);
        bellGain.gain.exponentialRampToValueAtTime(0.01, startTime + 0.3);

        bell.start(startTime);
        bell.stop(startTime + 0.3);
      });

      // Libérer après 5.5 secondes
      setTimeout(() => {
        isPlayingRef.current = false;
        if (audioContextRef.current) {
          audioContextRef.current.close();
          audioContextRef.current = null;
        }
      }, 5500);

    } catch (error) {
      console.log('Could not play notification sound:', error);
      isPlayingRef.current = false;
    }
  }, []);

  // Fonction pour arrêter le son manuellement
  const stopNotificationSound = useCallback(() => {
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    isPlayingRef.current = false;
  }, []);

  return { playNotificationSound, stopNotificationSound };
};

export default useNotificationSound;
